import { useEffect, useRef, useCallback } from 'react';

interface UseUnsavedChangesOptions {
  hasUnsavedChanges: boolean;
  onSave?: () => Promise<void> | void;
  onDiscard?: () => void;
}

export const useUnsavedChanges = ({
  hasUnsavedChanges,
  onSave,
  onDiscard
}: UseUnsavedChangesOptions) => {
  const beforeUnloadRef = useRef<(e: BeforeUnloadEvent) => void>();
  
  // Handle browser refresh/close
  const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
    if (hasUnsavedChanges) {
      e.preventDefault();
      e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      return e.returnValue;
    }
  }, [hasUnsavedChanges]);

  // Intercept navigation attempts
  const interceptNavigation = useCallback((
    targetUrl: string,
    showConfirmation: (options: {
      message: string;
      onConfirm: () => void;
      onCancel: () => void;
      showSaveOption?: boolean;
      onSave?: () => Promise<void> | void;
    }) => void
  ) => {
    if (hasUnsavedChanges) {
      showConfirmation({
        message: 'You have unsaved campaign changes. What would you like to do?',
        onConfirm: () => {
          if (onDiscard) onDiscard();
          window.location.href = targetUrl;
        },
        onCancel: () => {
          // Do nothing, stay on current page
        },
        showSaveOption: !!onSave,
        onSave: async () => {
          if (onSave) {
            await onSave();
            window.location.href = targetUrl;
          }
        }
      });
      return true; // Indicate navigation was intercepted
    }
    return false; // Allow navigation to proceed
  }, [hasUnsavedChanges, onSave, onDiscard]);

  // Set up beforeunload listener
  useEffect(() => {
    beforeUnloadRef.current = handleBeforeUnload;
    
    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, handleBeforeUnload]);

  return {
    interceptNavigation
  };
};